// src/pages/PrivacyPolicy.jsx
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const backTo = location.state?.from || "/";

  const LAST_UPDATED = "January 12, 2026";
  const APP_NAME = "Live Scenathon";

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {LAST_UPDATED}
            </p>
          </div>

          <button
            onClick={() => navigate(backTo)}
            className="text-sm font-medium text-green-600 hover:text-green-700"
          >
            Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border p-6 sm:p-8">
          <p className="text-gray-700 leading-relaxed">
            This Privacy Policy describes how <strong>{APP_NAME}</strong>{" "}
            processes users' personal data when registering for and using the
            Application.
          </p>

          <Section title="1. Data controller">
            <p className="text-gray-700 leading-relaxed">
              The data controller is the owner/administrator of the
              Application. Contact details are provided within the Application
              or through the available communication channels.
            </p>
          </Section>

          <Section title="2. Data we collect">
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              <li>
                <strong>Account data</strong>: name, email address, password
                (stored as a hash), phone number.
              </li>
              <li>
                <strong>Profile/organization data</strong>: organization,
                state and municipality (or other information provided during
                registration).
              </li>
              <li>
                <strong>Verification data</strong>: email verification code and
                its validity period.
              </li>
              <li>
                <strong>Technical and security data</strong>: session logs (for
                example, date/time), refresh token stored as a hash in the
                database, user agent, and approximate IP address for security
                purposes.
              </li>
            </ul>
          </Section>

          <Section title="3. Purposes of processing">
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              <li>Create and manage your user account.</li>
              <li>Verify your email address and prevent fraud.</li>
              <li>Enable login and keep the session active.</li>
              <li>
                Protect the Application (security, abuse prevention, auditing,
                traceability).
              </li>
              <li>
                Operational communications (for example, verification and
                security emails).
              </li>
            </ul>
          </Section>

          <Section title="4. Legal basis">
            <p className="text-gray-700 leading-relaxed">
              Processing is carried out to provide the service requested by the
              user (account management and access), as well as based on the
              legitimate interest of maintaining security and preventing misuse.
              Where required by applicable law, processing will be carried out
              with the user's consent.
            </p>
          </Section>

          <Section title="5. Data retention">
            <p className="text-gray-700 leading-relaxed">
              We retain data for as long as necessary to provide the service
              and comply with legal obligations. In particular:
            </p>
            <ul className="list-disc pl-5 text-gray-700 space-y-2 mt-3">
              <li>
                Account data: while the account remains active or as required
                by law.
              </li>
              <li>
                Verification records: only for the time strictly necessary to
                verify the email and manage incidents.
              </li>
              <li>
                Session/refresh tokens: until expiration, revocation, or as
                required by security policies.
              </li>
            </ul>
          </Section>

          <Section title="6. Recipients and third parties">
            <p className="text-gray-700 leading-relaxed">
              We do not sell personal data. We may use technical providers
              necessary to operate the Application (for example, email services
              for verification). These providers act under instructions and with
              reasonable security measures.
            </p>
          </Section>

          <Section title="7. International transfers">
            <p className="text-gray-700 leading-relaxed">
              If providers located outside the user's country are used,
              international transfers may occur. In such cases, appropriate
              safeguards will be applied in accordance with applicable law.
            </p>
          </Section>

          <Section title="8. Security">
            <p className="text-gray-700 leading-relaxed">
              We apply technical and organizational measures to protect data,
              including encryption in transit (HTTPS), hashed passwords, and
              session mechanisms (technical cookies and tokens). No system is
              completely infallible; users must also protect their credentials.
            </p>
          </Section>

          <Section title="9. User rights">
            <p className="text-gray-700 leading-relaxed">
              Under applicable law, users may request access, rectification,
              deletion, objection, or restriction of processing. To exercise
              these rights, contact us through the channels indicated in the
              Application.
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy. Changes will take effect upon
              publication in the Application.
            </p>
          </Section>
        </div>

        <p className="text-xs text-gray-500 mt-6">
          Note: This is a general informational document. For specific cases,
          seek professional advice.
        </p>
      </main>
    </div>
  );
};

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default PrivacyPolicy;
